import { useDownloadFiscalNote } from '@/api/accounting-dealer'
import { InfoItem, FiscalNoteStatus } from '@/components'
import { formatCurrency, formatDate } from '@/helpers'
import { routes } from '@/router'
import { FiscalNote, FiscalNoteFileType } from '@/types'
import { FileCopy, MoreHoriz } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Accordion,
  AccordionSummary,
  Box,
  Typography,
  AccordionDetails,
  AccordionProps,
  AccordionActions,
  Menu,
  MenuItem,
} from '@mui/material'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { SendEmailDialog } from './send-email-dialog'

type FiscalNoteProps = Omit<AccordionProps, 'children'> & {
  fiscalNote: FiscalNote
}

export function FiscalNoteAccordion({ fiscalNote, ...props }: FiscalNoteProps) {
  const [actionsMenuEl, setActionsMenuEl] = useState<null | HTMLElement>(null)
  const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false)

  const { mutateAsync: downloadFiscalNote, isPending: downloadingFiscalNote } =
    useDownloadFiscalNote({
      onSuccess: (file_url) =>
        window.open(file_url, '_blank', 'noopener, noreferrer'),
    })

  const closeActionsMenu = () => {
    setActionsMenuEl(null)
  }

  const handleDownloadFiscalNote = (
    fiscalNoteKey: string,
    fileType: FiscalNoteFileType,
  ) => {
    downloadFiscalNote({ file_type: fileType, fiscal_note_key: fiscalNoteKey })
    closeActionsMenu()
  }

  const handleSendEmail = () => {
    setSendEmailDialogOpen(true)
    closeActionsMenu()
  }

  const {
    borrower,
    value,
    status,
    serial_number,
    emission_date,
    description,
    key,
  } = fiscalNote

  const showAccordionActions = ['emitted', 'canceled'].includes(status)

  return (
    <Accordion
      disableGutters
      sx={{
        padding: 0,
        mx: '2px',
        borderRadius: 1,
        '&::before': { display: 'none' },
      }}
      {...props}
    >
      <AccordionSummary>
        <Box display="flex" alignItems="center" gap={2} flexGrow={1}>
          <FiscalNoteStatus status={status} type="dot" />

          <Typography flexGrow={1} noWrap width={0}>
            {borrower.name}
          </Typography>
          <Typography variant="h6">{formatCurrency(value)}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <InfoItem
          label="Status"
          value={<FiscalNoteStatus status={status} type="chip" />}
        />

        <InfoItem
          label="Cliente"
          value={`${borrower.name} - ${borrower.tax_id}`}
        />
        <InfoItem label="N°" value={serial_number} />
        <InfoItem label="Data de emissão" value={formatDate(emission_date)} />
        <InfoItem label="Serviço" value={description} />
      </AccordionDetails>

      {showAccordionActions && (
        <AccordionActions>
          <LoadingButton
            component={Link}
            to={routes.fiscalNotes.issue}
            state={{
              fiscalNote,
            }}
            variant="outlined"
            startIcon={<FileCopy />}
            fullWidth
            size="small"
            disabled={status !== 'emitted'}
          >
            Replicar
          </LoadingButton>
          <LoadingButton
            variant="outlined"
            startIcon={<MoreHoriz />}
            fullWidth
            size="small"
            onClick={(ev) => setActionsMenuEl(ev.currentTarget)}
            loading={downloadingFiscalNote}
          >
            Mais ações
          </LoadingButton>

          <Menu
            anchorEl={actionsMenuEl}
            open={Boolean(actionsMenuEl)}
            onClose={closeActionsMenu}
          >
            <MenuItem onClick={handleSendEmail} disabled={status !== 'emitted'}>
              Enviar por e-mail
            </MenuItem>
            <MenuItem onClick={() => handleDownloadFiscalNote(key, 'pdf')}>
              Baixar PDF
            </MenuItem>
            <MenuItem onClick={() => handleDownloadFiscalNote(key, 'xml')}>
              Baixar XML
            </MenuItem>
          </Menu>
        </AccordionActions>
      )}

      <SendEmailDialog
        open={sendEmailDialogOpen}
        onClose={() => setSendEmailDialogOpen(false)}
        fiscalNoteKey={key}
      />
    </Accordion>
  )
}
